import { useEffect } from 'react';
import { MarkupEditor } from '@/components/markup/MarkupEditor';
import { Button } from '@/components/atoms/Button/Button';
import { SurfaceProvider } from '@/theme/SurfaceContext';
import type { MarkupEditorModalProps } from './MarkupEditorModal.types';
import logger from '@/utils/logger';

export function MarkupEditorModal({
  isOpen,
  onClose,
  photo,
  shapes,
  onShapesChange,
  onCommit,
}: MarkupEditorModalProps) {
  const handleCommit = () => {
    logger.info('MarkupEditorModal', 'Committing markup with shapes:', shapes.length);
    onCommit();
  };

  const handleCancel = () => {
    logger.info('MarkupEditorModal', 'Canceling markup editor');
    onClose();
  };

  // Handle ESC key to cancel
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleCancel();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Don't render if not open
  if (!isOpen) {
    return null;
  }

  const canCommit = shapes.length > 0;

  return (
    <div className="fixed inset-0 z-50 bg-background/95 flex flex-col">
      {/* Header Toolbar */}
      <SurfaceProvider level="base" theme="light">
        <div className="flex items-center justify-between px-6 py-4 bg-surface-1 border-b border-border">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Markup Annotation</h2>
            <p className="text-sm text-muted mt-1">Draw shapes to highlight areas for critique</p>
          </div>

          <div className="flex gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={handleCancel}
            >
              Cancel (Esc)
            </Button>

            <Button
              type="button"
              variant="primary"
              onClick={handleCommit}
              disabled={!canCommit}
            >
              Commit Markup
            </Button>
          </div>
        </div>
      </SurfaceProvider>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden min-h-0">
        <div className="flex flex-col">
          <div className="flex-1 px-6 py-4 min-h-0">
            <div className="h-full bg-surface-2 rounded-lg p-6 min-h-0">
              <MarkupEditor
                photo={photo}
                shapes={shapes}
                className="w-full h-full"
                onShapesChange={onShapesChange}
                onValidationError={(msg) => {
                  logger.error('MarkupEditorModal', 'Validation error:', msg);
                  alert(msg);
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
